// packages/frontend/src/components/CGMonthlyStats.tsx
import { useEffect, useMemo, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../apiHelper';

// --- Types pour la vue détaillée (un seul mois) ---

interface CGDetailRow {
  registrationId: number;
  dealId: number;
  regPlate: string;
  createdAt: string;
  agencyId: number;
  agencyName: string;
  franchiseId: string | null;
  cgFee: number;
  invoiceStatus: string | null;
  invoiceNumber: string | null;
  invoicingType: string | null;
  clientId: string | null;
}

interface AgencyStats {
  agencyId: number;
  agencyName: string;
  franchiseId: string | null;
  count: number;
  cgFee?: number;
  totalValue?: number;
}

interface CGMonthlyStatsData {
  totalCG: number;
  cgByAgency: AgencyStats[];
  totalValue: number;
  valueByAgency: AgencyStats[];
  paymentStatus: {
    paid: { count: number; totalValue: number };
    unpaid: { count: number; totalValue: number };
    pendingInvoice: { count: number; totalValue: number };
  };
  cgDetails: CGDetailRow[];
}

// --- Types pour la vue évolution multi-mois ---

interface AgencyMonthData {
  cgCount: number;
  unpaidCount: number;
  unpaidValue: number;
  paidCount: number;
  paidValue: number;
  commissions: number;
}

interface AgencyEvolution {
  agencyId: number;
  agencyName: string;
  franchiseId: string | null;
  monthlyData: Record<string, AgencyMonthData>;
}

interface MonthData {
  year: number;
  month: number;
  label: string;
  totalCG: number;
  cgFeesTotal: number;
  paid: { count: number; value: number };
  unpaid: { count: number; value: number };
  pending: { count: number; value: number };
}

interface MonthlyRangeStats {
  months: MonthData[];
  agencies: AgencyEvolution[];
}

interface AvailableMonth {
  year: number;
  month: number;
  count: number;
}

const MONTH_NAMES = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

const stickyFirstColStyle = {
  ...stickyCellStyle,
  left: 0,
  zIndex: 200,
  minWidth: 180,
};

const frozenCellStyle = {
  position: 'sticky',
  left: 0,
  background: 'white',
  zIndex: 50,
  minWidth: 180,
  borderRight: '2px solid #e0e0e0',
};

const getStatusChip = (status: string | null) => {
  if (!status) {
    return <Chip label="En attente de facturation" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#666' }} />;
  }
  switch (status) {
    case 'paid':
      return <Chip label="Payé" size="small" sx={{ backgroundColor: '#c8e6c9', color: '#2e7d32' }} />;
    case 'pending':
    case 'unpaid':
      return <Chip label="Impayé" size="small" sx={{ backgroundColor: '#ffcdd2', color: '#c62828' }} />;
    case 'draft':
      return <Chip label="Brouillon" size="small" sx={{ backgroundColor: '#fff3e0', color: '#e65100' }} />;
    default:
      return <Chip label={status} size="small" />;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type ViewMode = 'evolution' | 'detail';

const CGMonthlyStats = () => {
  const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Evolution view state
  const [startMonth, setStartMonth] = useState<string>('');
  const [endMonth, setEndMonth] = useState<string>('');
  const [rangeData, setRangeData] = useState<MonthlyRangeStats | null>(null);
  const [isLoadingRange, setIsLoadingRange] = useState<boolean>(false);

  // Detail view state
  const [viewMode, setViewMode] = useState<ViewMode>('evolution');
  const [detailMonth, setDetailMonth] = useState<string>('');
  const [detailData, setDetailData] = useState<CGMonthlyStatsData | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  // Fetch available months on mount
  useEffect(() => {
    const fetchAvailableMonths = async () => {
      try {
        const response = await makeAuthenticatedApiCall('get', '/api/cg/stats/available-months');
        const data = response.data as { success: boolean; data: AvailableMonth[] };
        if (data.success) {
          const validMonths = data.data.filter((m) => m.year && m.month && m.month >= 1 && m.month <= 12);
          setAvailableMonths(validMonths);
          // Default: last 3 months
          if (validMonths.length >= 3) {
            setEndMonth(`${validMonths[0].year}-${validMonths[0].month}`);
            setStartMonth(`${validMonths[2].year}-${validMonths[2].month}`);
          } else if (validMonths.length > 0) {
            const last = validMonths[validMonths.length - 1];
            setEndMonth(`${validMonths[0].year}-${validMonths[0].month}`);
            setStartMonth(`${last.year}-${last.month}`);
          }
        }
      } catch (err) {
        console.error('Error fetching available months:', err);
        setError('Erreur lors de la récupération des mois disponibles');
      }
    };
    fetchAvailableMonths();
  }, []);

  // Fetch range stats when start/end changes
  useEffect(() => {
    const fetchRangeStats = async () => {
      if (!startMonth || !endMonth) return;

      const [sY, sM] = startMonth.split('-').map(Number);
      const [eY, eM] = endMonth.split('-').map(Number);

      if (sY > eY || (sY === eY && sM > eM)) {
        setError('Le mois de début doit être antérieur au mois de fin');
        setRangeData(null);
        return;
      }

      setIsLoadingRange(true);
      setError(null);

      try {
        const response = await makeAuthenticatedApiCall(
          'get',
          `/api/cg/stats/monthly-range?startYear=${sY}&startMonth=${sM}&endYear=${eY}&endMonth=${eM}`,
        );
        const data = response.data as { success: boolean; data: MonthlyRangeStats };
        if (data.success) {
          setRangeData(data.data);
        }
      } catch (err) {
        console.error('Error fetching range stats:', err);
        setError('Erreur lors de la récupération des statistiques');
      } finally {
        setIsLoadingRange(false);
      }
    };

    fetchRangeStats();
  }, [startMonth, endMonth]);

  // Fetch detail stats when switching to detail view
  useEffect(() => {
    const fetchDetail = async () => {
      if (viewMode !== 'detail' || !detailMonth) return;

      setIsLoadingDetail(true);
      const [year, month] = detailMonth.split('-').map(Number);

      try {
        const response = await makeAuthenticatedApiCall('get', `/api/cg/stats/monthly?year=${year}&month=${month}`);
        const data = response.data as { success: boolean; data: CGMonthlyStatsData };
        if (data.success) {
          setDetailData(data.data);
        }
      } catch (err) {
        console.error('Error fetching detail stats:', err);
        setError('Erreur lors de la récupération du détail');
      } finally {
        setIsLoadingDetail(false);
      }
    };

    fetchDetail();
  }, [viewMode, detailMonth]);

  const handleClickMonth = (year: number, month: number) => {
    setDetailMonth(`${year}-${month}`);
    setViewMode('detail');
  };

  const handleBackToEvolution = () => {
    setViewMode('evolution');
    setDetailData(null);
  };

  // Compute period totals for summary cards
  const periodTotals = useMemo(() => {
    if (!rangeData) return null;
    return rangeData.months.reduce(
      (acc, m) => ({
        totalCG: acc.totalCG + m.totalCG,
        cgFeesTotal: acc.cgFeesTotal + m.cgFeesTotal,
        paidCount: acc.paidCount + m.paid.count,
        paidValue: acc.paidValue + m.paid.value,
        unpaidCount: acc.unpaidCount + m.unpaid.count,
        unpaidValue: acc.unpaidValue + m.unpaid.value,
        pendingCount: acc.pendingCount + m.pending.count,
        pendingValue: acc.pendingValue + m.pending.value,
      }),
      { totalCG: 0, cgFeesTotal: 0, paidCount: 0, paidValue: 0, unpaidCount: 0, unpaidValue: 0, pendingCount: 0, pendingValue: 0 },
    );
  }, [rangeData]);

  // Sort agencies by total unpaid (descending) for the unpaid table
  const agenciesSortedByUnpaid = useMemo(() => {
    if (!rangeData) return [];
    return [...rangeData.agencies].sort((a, b) => {
      const totalUnpaidA = Object.values(a.monthlyData).reduce((sum, m) => sum + m.unpaidCount, 0);
      const totalUnpaidB = Object.values(b.monthlyData).reduce((sum, m) => sum + m.unpaidCount, 0);
      return totalUnpaidB - totalUnpaidA;
    });
  }, [rangeData]);

  // Filter agencies that have at least 1 unpaid
  const agenciesWithUnpaid = useMemo(() => {
    return agenciesSortedByUnpaid.filter((a) => Object.values(a.monthlyData).some((m) => m.unpaidCount > 0));
  }, [agenciesSortedByUnpaid]);

  if (error && !rangeData && !detailData && viewMode === 'evolution' && !startMonth) {
    return (
      <Container maxWidth={false} sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // ======================= DETAIL VIEW =======================
  if (viewMode === 'detail') {
    const [dYear, dMonth] = detailMonth.split('-').map(Number);
    const monthLabel = `${MONTH_NAMES[dMonth - 1]} ${dYear}`;

    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Statistiques CG Mensuelles
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToEvolution}
            sx={{
              color: '#464646',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: 'rgba(253, 187, 95, 0.1)' },
            }}
          >
            Retour à l'évolution
          </Button>
          <Typography variant="h5" sx={{ mt: 1, fontWeight: 600, color: '#262626' }}>
            Détail — {monthLabel}
          </Typography>
        </Box>

        {isLoadingDetail ? (
          <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
            <Typography sx={{ mb: 2 }}>Chargement du détail...</Typography>
            <CircularProgress size={70} />
          </Grid>
        ) : detailData ? (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ backgroundColor: 'rgba(253, 209, 95, 0.3)', height: '100%', border: '1px solid #FDD15F' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Volume</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#262626', mt: 1 }}>{detailData.totalCG}</Typography>
                    <Typography variant="body2" sx={{ color: '#464646' }}>CG créées ce mois</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ backgroundColor: 'rgba(253, 187, 95, 0.3)', height: '100%', border: '1px solid #FDBB5F' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Valeur</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#262626', mt: 1 }}>{detailData.totalValue.toFixed(2)} €</Typography>
                    <Typography variant="body2" sx={{ color: '#464646' }}>Montant total (frais CG payés)</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ backgroundColor: 'rgba(239, 143, 53, 0.2)', height: '100%', border: '1px solid #EF8F35' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Statut de paiement</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#2e7d32' }}>Payé :</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          {detailData.paymentStatus.paid.count} ({detailData.paymentStatus.paid.totalValue.toFixed(2)} €)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#FF6060' }}>Impayé :</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#FF6060' }}>
                          {detailData.paymentStatus.unpaid.count} ({detailData.paymentStatus.unpaid.totalValue.toFixed(2)} €)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#464646' }}>En attente :</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#464646' }}>
                          {detailData.paymentStatus.pendingInvoice.count} ({detailData.paymentStatus.pendingInvoice.totalValue.toFixed(2)} €)
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mb: 4 }}>
              {/* Volume by Agency */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Volume par agence</Typography>
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyCellStyle}>Agence</TableCell>
                      <TableCell sx={stickyCellStyle}>Franchise</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Nombre de CG</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailData.cgByAgency.map((agency) => (
                      <TableRow key={agency.agencyId}>
                        <TableCell>{agency.agencyName}</TableCell>
                        <TableCell>{agency.franchiseId || '-'}</TableCell>
                        <TableCell align="right">{agency.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Value by Agency */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Valeur par agence (avec frais CG par franchise)</Typography>
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyCellStyle}>Agence</TableCell>
                      <TableCell sx={stickyCellStyle}>Franchise</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Nombre de CG</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Frais CG unitaire</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Valeur totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailData.valueByAgency.map((agency) => (
                      <TableRow key={agency.agencyId}>
                        <TableCell>{agency.agencyName}</TableCell>
                        <TableCell>{agency.franchiseId || '-'}</TableCell>
                        <TableCell align="right">{agency.count}</TableCell>
                        <TableCell align="right">{(agency.cgFee || 10).toFixed(2)} €</TableCell>
                        <TableCell align="right">{(agency.totalValue || 0).toFixed(2)} €</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Detailed List */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Détail des CG ({detailData.cgDetails.length})</Typography>
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyCellStyle}>Immatriculation</TableCell>
                      <TableCell sx={stickyCellStyle}>Deal ID</TableCell>
                      <TableCell sx={stickyCellStyle}>Date création</TableCell>
                      <TableCell sx={stickyCellStyle}>Agence</TableCell>
                      <TableCell sx={stickyCellStyle}>Franchise</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Frais CG</TableCell>
                      <TableCell sx={stickyCellStyle}>Type facturation</TableCell>
                      <TableCell sx={stickyCellStyle}>N° facture</TableCell>
                      <TableCell sx={stickyCellStyle}>Statut paiement</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailData.cgDetails.map((cg) => (
                      <TableRow
                        key={cg.registrationId}
                        sx={{
                          backgroundColor:
                            cg.invoiceStatus === 'paid'
                              ? 'rgba(200, 230, 201, 0.3)'
                              : cg.invoiceStatus === 'pending' || cg.invoiceStatus === 'unpaid'
                                ? 'rgba(255, 205, 210, 0.3)'
                                : 'inherit',
                        }}
                      >
                        <TableCell>{cg.regPlate || '-'}</TableCell>
                        <TableCell>{cg.dealId}</TableCell>
                        <TableCell>{formatDate(cg.createdAt)}</TableCell>
                        <TableCell>{cg.agencyName}</TableCell>
                        <TableCell>{cg.franchiseId || '-'}</TableCell>
                        <TableCell align="right">{cg.cgFee.toFixed(2)} €</TableCell>
                        <TableCell>{cg.invoicingType || '-'}</TableCell>
                        <TableCell>{cg.invoiceNumber || '-'}</TableCell>
                        <TableCell>{getStatusChip(cg.invoiceStatus)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        ) : null}
      </Container>
    );
  }

  // ======================= EVOLUTION VIEW =======================
  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Statistiques CG Mensuelles — Évolution
      </Typography>

      {/* Month range selectors */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel className="inputElement">Mois de début</InputLabel>
          <Select
            className="inputElement"
            value={startMonth}
            onChange={(e: SelectChangeEvent<string>) => setStartMonth(e.target.value)}
            label="Mois de début"
          >
            {availableMonths.map((m) => (
              <MenuItem key={`start-${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                {MONTH_NAMES[m.month - 1]} {m.year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel className="inputElement">Mois de fin</InputLabel>
          <Select
            className="inputElement"
            value={endMonth}
            onChange={(e: SelectChangeEvent<string>) => setEndMonth(e.target.value)}
            label="Mois de fin"
          >
            {availableMonths.map((m) => (
              <MenuItem key={`end-${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                {MONTH_NAMES[m.month - 1]} {m.year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
          Cliquez sur un mois dans les tableaux pour voir le détail
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {isLoadingRange ? (
        <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
          <Typography sx={{ mb: 2 }}>Chargement des statistiques...</Typography>
          <CircularProgress size={70} />
        </Grid>
      ) : rangeData && periodTotals ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ backgroundColor: 'rgba(253, 209, 95, 0.3)', height: '100%', border: '1px solid #FDD15F' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Volume total</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#262626', mt: 1 }}>{periodTotals.totalCG}</Typography>
                  <Typography variant="body2" sx={{ color: '#464646' }}>CG sur la période</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ backgroundColor: 'rgba(253, 187, 95, 0.3)', height: '100%', border: '1px solid #FDBB5F' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Commissions facturées</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#262626', mt: 1 }}>{periodTotals.cgFeesTotal.toFixed(0)} €</Typography>
                  <Typography variant="body2" sx={{ color: '#464646' }}>Total frais CG facturés</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ backgroundColor: 'rgba(200, 230, 201, 0.3)', height: '100%', border: '1px solid #81c784' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>Payé</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#2e7d32', mt: 1 }}>{periodTotals.paidValue.toFixed(0)} €</Typography>
                  <Typography variant="body2" sx={{ color: '#464646' }}>{periodTotals.paidCount} CG payées</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ backgroundColor: 'rgba(255, 205, 210, 0.3)', height: '100%', border: '1px solid #e57373' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#c62828' }}>Impayé</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#c62828', mt: 1 }}>{periodTotals.unpaidValue.toFixed(0)} €</Typography>
                  <Typography variant="body2" sx={{ color: '#464646' }}>{periodTotals.unpaidCount} CG impayées</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 4 }}>
            {/* Évolution des impayés par agence */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Évolution des impayés par agence ({agenciesWithUnpaid.length} agences)
            </Typography>
            <Box sx={{ mb: 4 }}>
                {agenciesWithUnpaid.length === 0 ? (
                  <Alert severity="success">Aucun impayé sur la période sélectionnée</Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...stickyFirstColStyle, borderRight: '2px solid #555' }}>Agence</TableCell>
                          {rangeData.months.map((m) => (
                            <TableCell
                              key={`unpaid-h-${m.year}-${m.month}`}
                              sx={{ ...stickyCellStyle, minWidth: 100, cursor: 'pointer' }}
                              align="center"
                              onClick={() => handleClickMonth(m.year, m.month)}
                            >
                              <Tooltip title="Cliquer pour voir le détail">
                                <span>{m.label}</span>
                              </Tooltip>
                            </TableCell>
                          ))}
                          <TableCell sx={{ ...stickyCellStyle, minWidth: 80, backgroundColor: '#1a1a1a' }} align="center">
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agenciesWithUnpaid.map((agency) => {
                          const totalUnpaid = Object.values(agency.monthlyData).reduce((s, m) => s + m.unpaidCount, 0);
                          const totalUnpaidVal = Object.values(agency.monthlyData).reduce((s, m) => s + m.unpaidValue, 0);
                          return (
                            <TableRow key={agency.agencyId}>
                              <TableCell sx={frozenCellStyle}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{agency.agencyName}</Typography>
                              </TableCell>
                              {rangeData.months.map((m) => {
                                const key = `${m.year}-${m.month}`;
                                const data = agency.monthlyData[key];
                                const hasUnpaid = data && data.unpaidCount > 0;
                                return (
                                  <TableCell
                                    key={`unpaid-${agency.agencyId}-${key}`}
                                    align="center"
                                    sx={{
                                      backgroundColor: hasUnpaid ? 'rgba(255, 205, 210, 0.4)' : 'inherit',
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: 'rgba(253, 187, 95, 0.2)' },
                                    }}
                                    onClick={() => handleClickMonth(m.year, m.month)}
                                  >
                                    {data && data.unpaidCount > 0 ? (
                                      <>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>
                                          {data.unpaidCount}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#c62828' }}>
                                          {data.unpaidValue.toFixed(0)} €
                                        </Typography>
                                      </>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: '#ccc' }}>-</Typography>
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell
                                align="center"
                                sx={{ backgroundColor: totalUnpaid > 0 ? 'rgba(255, 205, 210, 0.6)' : 'inherit', fontWeight: 700 }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>{totalUnpaid}</Typography>
                                <Typography variant="caption" sx={{ color: '#c62828' }}>{totalUnpaidVal.toFixed(0)} €</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
            </Box>

            {/* Volume par agence - évolution */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Volume par agence — Évolution ({rangeData.agencies.length} agences)
            </Typography>
            <Box sx={{ mb: 4 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...stickyFirstColStyle, borderRight: '2px solid #555' }}>Agence</TableCell>
                        {rangeData.months.map((m) => (
                          <TableCell
                            key={`vol-h-${m.year}-${m.month}`}
                            sx={{ ...stickyCellStyle, minWidth: 80, cursor: 'pointer' }}
                            align="center"
                            onClick={() => handleClickMonth(m.year, m.month)}
                          >
                            <Tooltip title="Cliquer pour voir le détail">
                              <span>{m.label}</span>
                            </Tooltip>
                          </TableCell>
                        ))}
                        <TableCell sx={{ ...stickyCellStyle, minWidth: 80, backgroundColor: '#1a1a1a' }} align="center">
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rangeData.agencies.map((agency) => {
                        const totalCG = Object.values(agency.monthlyData).reduce((s, m) => s + m.cgCount, 0);
                        return (
                          <TableRow key={agency.agencyId}>
                            <TableCell sx={frozenCellStyle}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{agency.agencyName}</Typography>
                            </TableCell>
                            {rangeData.months.map((m) => {
                              const key = `${m.year}-${m.month}`;
                              const data = agency.monthlyData[key];
                              return (
                                <TableCell
                                  key={`vol-${agency.agencyId}-${key}`}
                                  align="center"
                                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(253, 187, 95, 0.2)' } }}
                                  onClick={() => handleClickMonth(m.year, m.month)}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: data && data.cgCount > 0 ? 600 : 400, color: data && data.cgCount > 0 ? '#262626' : '#ccc' }}>
                                    {data ? data.cgCount : 0}
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: 'rgba(253, 209, 95, 0.15)' }}>
                              {totalCG}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total row */}
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ ...frozenCellStyle, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>TOTAL</Typography>
                        </TableCell>
                        {rangeData.months.map((m) => (
                          <TableCell key={`vol-total-${m.year}-${m.month}`} align="center">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.totalCG}</Typography>
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: 'rgba(253, 209, 95, 0.3)' }}>
                          {periodTotals.totalCG}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
            </Box>

            {/* Rentabilité - Commissions CG */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Rentabilité — Commissions CG
            </Typography>
            <Box sx={{ mb: 4 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={stickyCellStyle}>Mois</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">CG</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">Commissions facturées</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">Payé</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">Impayé</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">En attente</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">Taux encaissement</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rangeData.months.map((m) => {
                        const encaissementRate = m.cgFeesTotal > 0 ? (m.paid.value / m.cgFeesTotal) * 100 : 0;
                        return (
                          <TableRow
                            key={`comm-${m.year}-${m.month}`}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(253, 187, 95, 0.1)' } }}
                            onClick={() => handleClickMonth(m.year, m.month)}
                          >
                            <TableCell sx={{ fontWeight: 600 }}>{m.label}</TableCell>
                            <TableCell align="right">{m.totalCG}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{m.cgFeesTotal.toFixed(2)} €</TableCell>
                            <TableCell align="right" sx={{ color: '#2e7d32' }}>
                              {m.paid.value.toFixed(2)} € ({m.paid.count})
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#c62828', fontWeight: m.unpaid.count > 0 ? 700 : 400 }}>
                              {m.unpaid.count > 0 ? `${m.unpaid.value.toFixed(2)} € (${m.unpaid.count})` : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#666' }}>
                              {m.pending.count > 0 ? `${m.pending.value.toFixed(2)} € (${m.pending.count})` : '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${encaissementRate.toFixed(0)}%`}
                                size="small"
                                sx={{
                                  backgroundColor: encaissementRate >= 95 ? '#c8e6c9' : encaissementRate >= 80 ? '#fff3e0' : '#ffcdd2',
                                  color: encaissementRate >= 95 ? '#2e7d32' : encaissementRate >= 80 ? '#e65100' : '#c62828',
                                  fontWeight: 700,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total row */}
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{periodTotals.totalCG}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{periodTotals.cgFeesTotal.toFixed(2)} €</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                          {periodTotals.paidValue.toFixed(2)} € ({periodTotals.paidCount})
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#c62828' }}>
                          {periodTotals.unpaidValue.toFixed(2)} € ({periodTotals.unpaidCount})
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#666' }}>
                          {periodTotals.pendingValue.toFixed(2)} € ({periodTotals.pendingCount})
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${periodTotals.cgFeesTotal > 0 ? ((periodTotals.paidValue / periodTotals.cgFeesTotal) * 100).toFixed(0) : 0}%`}
                            size="small"
                            sx={{ fontWeight: 700, backgroundColor: '#e3f2fd', color: '#1565c0' }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
            </Box>
          </Box>
        </>
      ) : null}
    </Container>
  );
};

export default CGMonthlyStats;
